import { useState } from 'react';
import DatePicker from '@/components/common/DatePicker';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Clock,
  Wrench,
  CheckCircle,
  XCircle,
  Truck,
  PackageX,
  Hourglass,
  AlertTriangle,
  Loader2,
  Calendar,
} from 'lucide-react';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Area, AreaChart } from 'recharts';
import { dashboardApi } from '@/services/dashboardApi';
import ActivityFeed from '@/components/dashboard/ActivityFeed';
import ServiceDashboard from '@/components/dashboard/ServiceDashboard';
import { format } from 'date-fns';
import { ServiceOverviewData } from '@/types';
import { CurrencyIcon } from '@/components/CurrencyIcon';
import { getActiveCurrency } from '@/utils/currency';

type DateFilter = 'today' | 'month' | 'custom';

export default function AdminDashboard() {
  const [dateFilter, setDateFilter] = useState<DateFilter>('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Service overview with branch-wise breakdown
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['service-overview', dateFilter, customStart, customEnd],
    queryFn: () =>
      dashboardApi.getBranchServiceOverview({
        filter: dateFilter === 'custom' ? 'custom' : dateFilter,
        startDate: dateFilter === 'custom' ? customStart : undefined,
        endDate: dateFilter === 'custom' ? customEnd : undefined,
      }),
    staleTime: 60 * 1000,
    refetchInterval: 90 * 1000,
    refetchOnWindowFocus: true,
  });

  // Charts + activity from existing endpoint
  const { data: dashData } = useQuery({
    queryKey: ['dashboardData'],
    queryFn: dashboardApi.getDashboardData,
    staleTime: 60 * 1000,
  });

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(getActiveCurrency().locale, {
      style: 'currency',
      currency: getActiveCurrency().code,
      maximumFractionDigits: 0,
    }).format(amount);

  const summary = overview?.summary;

  return (
    <>
      {/* Service Dashboard (per-branch stats with date filter) */}
      <div className="mb-6">
        <ServiceDashboard />
      </div>

      {/* Services Overview Header + Date Filter */}
      <div className="bg-white rounded-xl border mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-bold text-gray-900">Services Overview</h2>
          <div className="flex items-center gap-2 mt-2 sm:mt-0">
            <button
              onClick={() => setDateFilter('today')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                dateFilter === 'today'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setDateFilter('month')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                dateFilter === 'month'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              This Month
            </button>
            <button
              onClick={() => setDateFilter('custom')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                dateFilter === 'custom'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Custom
            </button>
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-1 ml-2">
                <DatePicker
                  value={customStart}
                  onChange={setCustomStart}
                  wrapperClassName="w-32"
                  className="!px-2 !py-1 !text-xs"
                />
                <span className="text-xs text-gray-400">to</span>
                <DatePicker
                  value={customEnd}
                  onChange={setCustomEnd}
                  wrapperClassName="w-32"
                  className="!px-2 !py-1 !text-xs"
                />
              </div>
            )}
          </div>
        </div>

        {overviewLoading || !summary ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="p-4">
            {/* Status Cards - Row 1 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
              <StatusCard
                label="Total"
                value={summary.totalServices}
                icon={BarChart3}
                color="purple"
              />
              <StatusCard
                label="Pending"
                value={summary.pending}
                icon={Clock}
                color="yellow"
              />
              <StatusCard
                label="In Progress"
                value={summary.inProgress}
                icon={Wrench}
                color="blue"
              />
              <StatusCard
                label="Waiting Parts"
                value={summary.waitingParts}
                icon={Hourglass}
                color="orange"
              />
              <StatusCard
                label="Ready"
                value={summary.ready}
                icon={CheckCircle}
                color="green"
              />
              <StatusCard
                label="Not Ready"
                value={summary.notReady}
                icon={XCircle}
                color="red"
              />
              <StatusCard
                label="Delivered"
                value={summary.delivered}
                icon={Truck}
                color="teal"
              />
              <StatusCard
                label="Undelivered"
                value={summary.notDelivered}
                icon={PackageX}
                color="gray"
              />
            </div>

            {/* Money Cards - Row 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="p-2.5 bg-green-100 rounded-lg">
                  <CurrencyIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-green-700">
                    {formatCurrency(summary.totalCollected)}
                  </p>
                  <p className="text-xs text-green-600">Total Collected</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="p-2.5 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-red-700">
                    {formatCurrency(summary.outstanding)}
                  </p>
                  <p className="text-xs text-red-600">Outstanding</p>
                </div>
              </div>
            </div>

            {/* Branch-wise Breakdown Table */}
            {overview.branches.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Branch-wise Breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-y">
                      <tr>
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">Branch</th>
                        <th className="px-2 py-2.5 text-center text-xs font-semibold text-purple-600 uppercase whitespace-nowrap">Total</th>
                        <th className="px-2 py-2.5 text-center text-xs font-semibold text-yellow-600 uppercase whitespace-nowrap">Pending</th>
                        <th className="px-2 py-2.5 text-center text-xs font-semibold text-blue-600 uppercase whitespace-nowrap">In Prog.</th>
                        <th className="px-2 py-2.5 text-center text-xs font-semibold text-orange-600 uppercase whitespace-nowrap">Waiting</th>
                        <th className="px-2 py-2.5 text-center text-xs font-semibold text-green-600 uppercase whitespace-nowrap">Ready</th>
                        <th className="px-2 py-2.5 text-center text-xs font-semibold text-red-600 uppercase whitespace-nowrap">Not Ready</th>
                        <th className="px-2 py-2.5 text-center text-xs font-semibold text-teal-600 uppercase whitespace-nowrap">Delivered</th>
                        <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Undeliv.</th>
                        <th className="px-2 py-2.5 text-right text-xs font-semibold text-green-600 uppercase whitespace-nowrap">Collected</th>
                        <th className="px-2 py-2.5 text-right text-xs font-semibold text-red-600 uppercase whitespace-nowrap">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {overview.branches.map((branch) => (
                        <tr key={branch.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2.5">
                            <p className="font-medium text-gray-900">{branch.name}</p>
                            <p className="text-xs text-gray-400">{branch.code}</p>
                          </td>
                          <td className="px-2 py-2.5 text-center font-semibold text-gray-900">{branch.totalServices}</td>
                          <BranchCell value={branch.pending} color="yellow" />
                          <BranchCell value={branch.inProgress} color="blue" />
                          <BranchCell value={branch.waitingParts} color="orange" />
                          <BranchCell value={branch.ready} color="green" />
                          <BranchCell value={branch.notReady} color="red" />
                          <BranchCell value={branch.delivered} color="teal" />
                          <BranchCell value={branch.notDelivered} color="gray" />
                          <td className="px-2 py-2.5 text-right">
                            <span className="text-sm font-medium text-green-700 whitespace-nowrap">
                              {formatCurrency(branch.totalCollected)}
                            </span>
                          </td>
                          <td className="px-2 py-2.5 text-right">
                            <span className={`text-sm font-medium whitespace-nowrap ${branch.outstanding > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {formatCurrency(branch.outstanding)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {/* Totals Row */}
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                      <tr className="font-semibold">
                        <td className="px-3 py-2.5 text-gray-900">Total</td>
                        <td className="px-2 py-2.5 text-center text-gray-900">{summary.totalServices}</td>
                        <td className="px-2 py-2.5 text-center text-yellow-700">{summary.pending}</td>
                        <td className="px-2 py-2.5 text-center text-blue-700">{summary.inProgress}</td>
                        <td className="px-2 py-2.5 text-center text-orange-700">{summary.waitingParts}</td>
                        <td className="px-2 py-2.5 text-center text-green-700">{summary.ready}</td>
                        <td className="px-2 py-2.5 text-center text-red-700">{summary.notReady}</td>
                        <td className="px-2 py-2.5 text-center text-teal-700">{summary.delivered}</td>
                        <td className="px-2 py-2.5 text-center text-gray-700">{summary.notDelivered}</td>
                        <td className="px-2 py-2.5 text-right text-green-700 whitespace-nowrap">{formatCurrency(summary.totalCollected)}</td>
                        <td className="px-2 py-2.5 text-right text-red-700 whitespace-nowrap">{formatCurrency(summary.outstanding)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Charts + Activity Feed */}
      {dashData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Charts */}
          <div className="lg:col-span-2 space-y-6">
            {/* Services Trend - Bar Chart */}
            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">Services Trend (Last 10 Days)</h3>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div>
                    <span className="text-gray-500">Services</span>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dashData.servicesChart}>
                  <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} />
                  <YAxis stroke="#9CA3AF" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  />
                  <Bar dataKey="value" fill="#7C3AED" radius={[6, 6, 0, 0]} name="Services" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Weekly Trend - Area Chart */}
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-semibold text-gray-800 mb-4">Weekly Trend</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={dashData.weeklyTrend}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" stroke="#9CA3AF" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#7C3AED" strokeWidth={2} fill="url(#colorValue)" name="Services" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Activity Feed */}
          <div className="bg-white rounded-xl border p-5">
            <ActivityFeed
              activities={dashData.recentActivity}
              title="Recent Activity"
              date={format(new Date(), 'do MMM, yyyy')}
            />
          </div>
        </div>
      )}
    </>
  );
}

// Status Card Component
function StatusCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  const colorMap: Record<string, { bg: string; text: string; iconBg: string }> = {
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', iconBg: 'bg-purple-100' },
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', iconBg: 'bg-yellow-100' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', iconBg: 'bg-blue-100' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-700', iconBg: 'bg-orange-100' },
    green: { bg: 'bg-green-50', text: 'text-green-700', iconBg: 'bg-green-100' },
    red: { bg: 'bg-red-50', text: 'text-red-700', iconBg: 'bg-red-100' },
    teal: { bg: 'bg-teal-50', text: 'text-teal-700', iconBg: 'bg-teal-100' },
    gray: { bg: 'bg-gray-50', text: 'text-gray-700', iconBg: 'bg-gray-200' },
  };

  const c = colorMap[color] || colorMap.gray;

  return (
    <div className={`${c.bg} rounded-xl p-3 border`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 ${c.iconBg} rounded-lg`}>
          <Icon className={`w-3.5 h-3.5 ${c.text}`} />
        </div>
      </div>
      <p className={`text-xl font-bold ${c.text}`}>{value}</p>
      <p className="text-[10px] text-gray-500 uppercase font-medium">{label}</p>
    </div>
  );
}

// Branch Table Cell
function BranchCell({ value, color }: { value: number; color: string }) {
  const colorMap: Record<string, string> = {
    yellow: 'bg-yellow-100 text-yellow-700',
    blue: 'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-orange-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    teal: 'bg-teal-100 text-teal-700',
    gray: 'bg-gray-200 text-gray-700',
  };

  return (
    <td className="px-2 py-2.5 text-center">
      <span
        className={`inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded text-xs font-medium ${
          value > 0 ? colorMap[color] || 'text-gray-400' : 'text-gray-400'
        }`}
      >
        {value}
      </span>
    </td>
  );
}
